import {useQuery} from '@tanstack/react-query';
import {getBenefitAvailable} from '@/services/commerce/benefit';
import {useUrlProjectId} from '@/hooks/useUrlProjectId';
import {useAuthStore} from '@/store/useAuthStore';
import {usePermissionsStore} from '@/store/usePermissionsStore';
import {queryClient} from '@/utils/queryClient';
import type {BenefitProfile} from '@/services/commerce/entities/benefit';

/**
 * 权益快照 Key Factory
 * 供 invalidateQueries 使用
 */
export const benefitKeys = {
    all: ['benefit.available'] as const,
    available: (projectId: string | null) => ['benefit.available', projectId] as const,
};

/**
 * 精确刷新权益快照（非 hook 环境可用）
 * - 始终刷新用户维度
 * - 当前在项目上下文中时，同时刷新项目维度
 * - 典型调用时机：支付成功后、拦截器兜底
 */
export const refreshBenefitProfile = (): void => {
    const currentProjectId = usePermissionsStore.getState().currentProjectId;

    // 刷新用户维度
    void queryClient.invalidateQueries({
        queryKey: benefitKeys.available(null),
        exact: true,
    });

    // 在项目上下文中时，同时刷新项目维度
    if (currentProjectId) {
        void queryClient.invalidateQueries({
            queryKey: benefitKeys.available(currentProjectId),
            exact: true,
        });
    }
};

const BENEFIT_QUERY_OPTIONS = {
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
};

/**
 * 用户维度权益快照
 * - queryKey: ['benefit.available', null]
 * - 用于首页、会员中心等不依赖项目上下文的场景
 */
export function useUserBenefitProfile() {
    const isLogin = useAuthStore((state) => state.isLogin);

    return useQuery<BenefitProfile>({
        queryKey: benefitKeys.available(null),
        queryFn: () => getBenefitAvailable({projectId: null}),
        enabled: isLogin,
        ...BENEFIT_QUERY_OPTIONS,
    });
}

/**
 * 项目维度权益快照
 * - queryKey: ['benefit.available', projectId]
 * - 用于项目内页面（工作台、画布、剧本等）
 * - projectId 仅从 URL 读取（useUrlProjectId），避免 store 残留导致首页误请求
 */
export function useProjectBenefitProfile() {
    const isLogin = useAuthStore((state) => state.isLogin);
    const projectId = useUrlProjectId();

    return useQuery<BenefitProfile>({
        queryKey: benefitKeys.available(projectId),
        queryFn: () => getBenefitAvailable({projectId}),
        enabled: isLogin && !!projectId,
        ...BENEFIT_QUERY_OPTIONS,
    });
}
